import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Stack,
  Box,
  DialogActions,
} from '@mui/material';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Radio from '@mui/material/Radio';
import { IoTime } from 'react-icons/io5';
import { FaTruck } from 'react-icons/fa';
import { environment } from '../../../environments/environment';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSelector } from 'react-redux';
import {
  getTomorrow,
  getTomorrowScheduleLater,
  formatTimeSlotForUser,
  convertUserTimeToUTC,
} from '../../Pages/CheckoutPage/utils';

const tomorrow = 'tomorrow';
const today = 'today';
const afterTomorrow = 'afterTomorrow';

const SchedulingDialog = ({
  open,
  onClose,
  onConfirm,
  store,
  orderType,
  isStoreOpen,
  titleOverride,
  subtitleOverride,
}) => {
  const [t, i18n] = useTranslation();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));

  // 'initial' shows ASAP / Schedule for later, 'schedule' shows the time picker
  const [step, setStep] = useState('initial');

  const [sendTime, setSendTime] = useState(today);
  const [selectedTime, setSelectedTime] = useState({});
  const [range, setRange] = useState('');
  const [prev, setPrev] = useState('');
  const [slots, setSlots] = useState({});
  const [realTime, setRealTime] = useState(null);
  const [dateOfOrder, setDateOfOrder] = useState(null);

  const hasCatering = false;

  const reduxCart = useSelector((state) => state.shopReducer.cart);
  const user = useSelector((state) => state.authReducer.userData);
  const categories = useSelector(
    (state) => state.shopReducer.currentCategories
  );

  const [hasItemAvailabilityConstraints, setHasItemAvailabilityConstraints] =
    useState(false);
  const [availabilityCategoryNames, setAvailabilityCategoryNames] = useState(
    []
  );
  const lastAvailabilityCategoryNamesRef = useRef([]);

  // Reset to initial step whenever dialog opens
  useEffect(() => {
    if (open) {
      setStep('initial');
    }
  }, [open]);

  const isNotAvailableOnline =
    store && !store.pickup_enabled && !store.delivery_enabled;

  const locale = i18n.language || 'en-US';
  const timeZone =
    (store && store.timezone) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formatDateLabel = (offset) => {
    const now = new Date();
    const target = new Date(now);
    target.setDate(target.getDate() + offset);
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(target);
  };
  const todayLabel = formatDateLabel(0);
  const tomorrowLabel = formatDateLabel(1);

  // ASAP ready time based on order type
  const calculateReadyTime = () => {
    const now = new Date();
    const preparationMinutes =
      orderType === 'delivery' || orderType === 'noDriverDelivery' ? 45 : 15;
    const readyTime = new Date(now.getTime() + preparationMinutes * 60 * 1000);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(readyTime);
  };

  // Calculate available time slots
  useEffect(() => {
    if (!store || !store.timing || !Array.isArray(store.timing)) {
      return;
    }

    const timezone = store.timezone || 'Etc/UTC';
    const now = new Date();

    const parseMinutes = (hhmmss) => {
      const [h, m] = (hhmmss || '')
        .split(':')
        .map((s) => parseInt(s || '0', 10));
      return (h || 0) * 60 + (m || 0);
    };

    const cartItems =
      (reduxCart && reduxCart.length ? reduxCart : user?.bagItems) || [];

    const cartCategoryIds = new Set();
    cartItems.forEach((item) => {
      if (item?.categoryId) cartCategoryIds.add(item.categoryId);
      if (item?.category_id) cartCategoryIds.add(item.category_id);
      if (Array.isArray(item?.category_ids)) {
        item.category_ids.forEach((cid) => cid && cartCategoryIds.add(cid));
      }
      if (item?.availabilityRootCategoryId) {
        cartCategoryIds.add(item.availabilityRootCategoryId);
      }
    });

    const getEffectiveAvailabilityPeriodsForCategory = (cat) => {
      let periods = cat?.availability_periods || cat?.availabilityPeriods || [];
      if ((!periods || periods.length === 0) && cat?.rootCategory) {
        const rootCategoryObj = (categories || []).find(
          (c) => c.id === cat.rootCategory
        );
        if (rootCategoryObj) {
          periods =
            rootCategoryObj.availability_periods ||
            rootCategoryObj.availabilityPeriods ||
            [];
        }
      }
      return periods || [];
    };

    const categoriesWithAvailability =
      (categories || []).filter((cat) => {
        if (!cartCategoryIds.has(cat.id)) return false;
        const periods = getEffectiveAvailabilityPeriodsForCategory(cat);
        return Array.isArray(periods) && periods.length > 0;
      }) || [];

    const hasConstraints =
      Array.isArray(categoriesWithAvailability) &&
      categoriesWithAvailability.length > 0;

    setHasItemAvailabilityConstraints(hasConstraints);

    if (hasConstraints) {
      const names = categoriesWithAvailability
        .map((cat) => cat?.displayName || cat?.name || cat?.title || cat?.label)
        .filter(Boolean);
      setAvailabilityCategoryNames(names);
      lastAvailabilityCategoryNamesRef.current = names;
    } else {
      setAvailabilityCategoryNames([]);
    }

    const timeToMinutes = (timeStr) => {
      if (!timeStr) return 0;
      const [h, m] = timeStr.split(':').map((s) => parseInt(s || '0', 10));
      return (h || 0) * 60 + (m || 0);
    };

    const isSlotAllowedForCart = (dayOfWeek, hhmm) => {
      if (!categoriesWithAvailability.length) return true;
      const slotMinutes = timeToMinutes(hhmm);

      return categoriesWithAvailability.every((category) => {
        const basePeriods =
          getEffectiveAvailabilityPeriodsForCategory(category);
        const activePeriods = basePeriods.filter(
          (p) => !p.isDeleted && p.type === 'AVAILABILITY_PERIOD'
        );
        if (!activePeriods.length) return true;

        return activePeriods.some((period) => {
          const availabilityData = period.availabilityPeriodData;
          if (!availabilityData) return false;

          const {
            dayOfWeek: periodDay,
            startLocalTime,
            endLocalTime,
          } = availabilityData;

          if (!periodDay || !startLocalTime || !endLocalTime) return false;
          if (periodDay.toUpperCase() !== dayOfWeek) return false;

          const startM = timeToMinutes(startLocalTime);
          const endM = timeToMinutes(endLocalTime);

          const spansMidnight = endM < startM;
          if (spansMidnight) {
            return slotMinutes >= startM || slotMinutes <= endM;
          }

          return slotMinutes >= startM && slotMinutes <= endM;
        });
      });
    };

    const generateSlotsForDay = (dayOffset) => {
      const targetDate = new Date(now);
      targetDate.setDate(targetDate.getDate() + dayOffset);

      const dayName = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short',
      })
        .format(targetDate)
        .toUpperCase();

      const dayTimings = store.timing.filter((t) => t.dayOfWeek === dayName);
      const mainTiming =
        dayTimings.find(
          (t) => t.startLocalTime && parseMinutes(t.startLocalTime) > 0
        ) || dayTimings[0];

      if (
        !mainTiming ||
        !mainTiming.startLocalTime ||
        !mainTiming.endLocalTime
      ) {
        return [];
      }

      const [startHour, startMinute] = mainTiming.startLocalTime
        .split(':')
        .map(Number);
      let [endHour, endMinute] = mainTiming.endLocalTime.split(':').map(Number);

      const dayOrder = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
      const todayIndex = dayOrder.indexOf(dayName);
      const nextDayKey = dayOrder[(todayIndex + 1) % 7];
      const nextDayTimings = store.timing.filter(
        (t) => t.dayOfWeek === nextDayKey
      );
      const nextDayOverflow = nextDayTimings.find(
        (t) => t.startLocalTime && parseMinutes(t.startLocalTime) === 0
      );

      if (
        endHour === 24 &&
        endMinute === 0 &&
        nextDayOverflow &&
        nextDayOverflow.endLocalTime
      ) {
        const [overflowEndHour, overflowEndMinute] =
          nextDayOverflow.endLocalTime.split(':').map(Number);
        endHour = 24 + overflowEndHour;
        endMinute = overflowEndMinute;
      }

      const startTimeInMinutes = startHour * 60 + startMinute;
      const endTimeInMinutes = endHour * 60 + endMinute - 15;

      const intervalMinutes = 15;
      const preparationTimeMinutes = 15;

      let earliestStartTime = startTimeInMinutes + preparationTimeMinutes;

      if (dayOffset === 0) {
        const currentTime = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(now);

        const [currentHour, currentMinutes] = currentTime
          .split(':')
          .map(Number);
        const currentTimeInMinutes = currentHour * 60 + currentMinutes;

        earliestStartTime = Math.max(
          earliestStartTime,
          currentTimeInMinutes + preparationTimeMinutes
        );
      }

      const roundUpToInterval = (minutes, interval) =>
        Math.ceil(minutes / interval) * interval;

      const firstSlotTime = roundUpToInterval(
        earliestStartTime,
        intervalMinutes
      );

      const timeSlots = [];
      for (
        let timeInMinutes = firstSlotTime;
        timeInMinutes <= endTimeInMinutes;
        timeInMinutes += intervalMinutes
      ) {
        let slotStartHour = Math.floor(timeInMinutes / 60);
        const slotStartMinute = timeInMinutes % 60;
        slotStartHour = slotStartHour % 24;

        const startTime = `${slotStartHour
          .toString()
          .padStart(2, '0')}:${slotStartMinute.toString().padStart(2, '0')}`;

        if (!isSlotAllowedForCart(dayName, startTime)) {
          continue;
        }

        const userTimeSlot = formatTimeSlotForUser(startTime, timezone);
        timeSlots.push(userTimeSlot);
      }

      return timeSlots;
    };

    const todaySlots = generateSlotsForDay(0);
    const tomorrowSlots = generateSlotsForDay(1);
    const afterTomorrowSlots = generateSlotsForDay(2);

    setSlots({
      asap: false,
      today: todaySlots,
      tomorrow: tomorrowSlots,
      afterTomorrow: afterTomorrowSlots,
    });
  }, [store, orderType, reduxCart, user, categories]);

  const onTimeSelected = (data, index) => {
    const currentSendTime = sendTime;
    setRealTime(currentSendTime);

    const startTimeStr = data.split(' - ')[0].trim();

    const [timeHourMin, period] =
      startTimeStr.includes('AM') || startTimeStr.includes('PM')
        ? startTimeStr.split(/\s+/)
        : [startTimeStr, null];

    const [hourStr, minuteStr] = timeHourMin.split(':');
    let hour = parseInt(hourStr);
    const minute = parseInt(minuteStr) || 0;

    if (period) {
      if (period.toUpperCase() === 'PM' && hour !== 12) {
        hour = hour + 12;
      } else if (period.toUpperCase() === 'AM' && hour === 12) {
        hour = 0;
      }
    }

    const targetDate = new Date();

    if (currentSendTime === tomorrow) {
      targetDate.setDate(targetDate.getDate() + 1);
    } else if (currentSendTime === afterTomorrow) {
      targetDate.setDate(targetDate.getDate() + 2);
    }

    targetDate.setHours(hour, minute, 0, 0);

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const utcDate = convertUserTimeToUTC(
      startTimeStr,
      userTimezone,
      targetDate
    );

    setDateOfOrder(utcDate);
    setSelectedTime({ index, data, sendTime: currentSendTime });
    setPrev(currentSendTime);
    setRange(index);
  };

  const handleAsapConfirm = () => {
    const readyTime = calculateReadyTime();
    onConfirm({
      isAsap: true,
      dateOfOrder: null,
      sendTime: 'asap',
      displayTime: readyTime,
    });
  };

  const handleScheduleConfirm = () => {
    if (!dateOfOrder) return;

    const formatSingleTime = (time) => {
      if (!time || typeof time !== 'string') return time;
      const t = time.trim();
      if (/AM|PM/i.test(t)) {
        const m = t.match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?\s*(AM|PM)$/i);
        if (m)
          return `${parseInt(m[1], 10)}:${String(
            parseInt(m[2], 10) || 0
          ).padStart(2, '0')} ${m[3].toUpperCase()}`;
      }
      const parts = t.split(':');
      let hour = parseInt(parts[0], 10);
      const minStr = (parts[1] || '00').replace(/\s*[AP]M$/i, '');
      const minute = String(parseInt(minStr, 10) || 0).padStart(2, '0');
      if (isNaN(hour)) return t;
      const period = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12;
      return `${hour}:${minute} ${period}`;
    };

    const formatTimeWithAMPM = (timeStr) => {
      if (!timeStr) return timeStr;
      if (timeStr.includes(' - ')) {
        const [start, end] = timeStr.split(' - ');
        return `${formatSingleTime(start.trim())} - ${formatSingleTime(
          end.trim()
        )}`;
      }
      return formatSingleTime(timeStr);
    };

    onConfirm({
      isAsap: false,
      dateOfOrder,
      selectedTime,
      sendTime: realTime || sendTime,
      displayTime: formatTimeWithAMPM(selectedTime.data),
    });
  };

  const handleClose = () => {
    setSelectedTime({});
    setRange('');
    setPrev('');
    setDateOfOrder(null);
    setSendTime(today);
    setStep('initial');
    onClose();
  };

  const statusText = isNotAvailableOnline
    ? 'This location is not available for online ordering.'
    : isStoreOpen
    ? 'This location is open and accepting orders'
    : 'This location is currently closed';

  // ── Initial step: ASAP / Schedule for Later ───────────────────────────────
  const renderInitialStep = () => (
    <>
      <DialogTitle
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '36px 24px 20px 24px',
          background: `linear-gradient(135deg, ${environment.mainColor}18, ${environment.mainColor}08)`,
          borderBottom: `1px solid ${environment.mainColor}20`,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            backgroundColor: environment.mainColor,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            boxShadow: `0px 8px 24px ${environment.mainColor}40`,
          }}
        >
          <IoTime size={28} style={{ color: 'white' }} />
        </Box>
        <Typography
          component="h2"
          variant="h5"
          style={{
            fontWeight: 700,
            color: '#1a1a1a',
            fontSize: '22px',
            fontFamily: 'Jost, sans-serif',
            marginBottom: 6,
            letterSpacing: '-0.3px',
          }}
        >
          {titleOverride || 'When do you want it?'}
        </Typography>
        <Typography
          variant="body2"
          style={{
            color: '#666666',
            fontSize: '13px',
            fontFamily: 'Jost, sans-serif',
            maxWidth: 260,
            lineHeight: 1.5,
          }}
        >
          {subtitleOverride || statusText}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: '32px 20px 8px 20px', backgroundColor: '#fafafa' ,  }}>
        <Stack spacing={1.5}>
          {/* ASAP option — only when store is open */}
          {isStoreOpen && !isNotAvailableOnline && (
            <Box
              onClick={handleAsapConfirm}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: '16px 18px',
                backgroundColor: '#fff',
                borderRadius: '14px',
                border: '1.5px solid #f0f0f0',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginTop:1.5,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                '&:hover': {
                  borderColor: environment.mainColor,
                  boxShadow: `0 4px 16px ${environment.mainColor}20`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: `${environment.mainColor}18`,
                  borderRadius: '10px',
                  display: 'flex',

                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FaTruck size={20} color={environment.mainColor} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '15px',
                    color: '#1a1a1a',
                    fontFamily: 'Jost, sans-serif',
                    lineHeight: 1.3,
                  }}
                >
                  ASAP
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: environment.mainColor,
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 500,
                    mt: 0.25,
                  }}
                >
                  {orderType === 'delivery' || orderType === 'noDriverDelivery'
                    ? `Delivered approximately within 45 min - 1 hour`
                    : `Ready to pickup approximately within 15 minutes`}
                </Typography>
              </Box>
              <Radio
                checked={false}
                size="small"
                sx={{ color: '#b0b0b0', p: 0, '&.Mui-checked': { color: environment.mainColor } }}
              />
            </Box>
          )}

          {/* Schedule for later */}
          <Box
            onClick={() => setStep('schedule')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: '16px 18px',
              backgroundColor: '#fff',
              borderRadius: '14px',
              border: '1.5px solid #f0f0f0',
              cursor: 'pointer',
              marginTop: isStoreOpen && !isNotAvailableOnline ? 0 : 1.5,
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              '&:hover': {
                borderColor: environment.mainColor,
                boxShadow: `0 4px 16px ${environment.mainColor}20`,
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                backgroundColor: `${environment.mainColor}18`,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CalendarTodayIcon sx={{ color: environment.mainColor, fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '15px',
                  color: '#1a1a1a',
                  fontFamily: 'Jost, sans-serif',
                  lineHeight: 1.3,
                }}
              >
                Schedule for later
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: environment.mainColor,
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 500,
                  mt: 0.25,
                }}
              >
                Pick a date and time slot
              </Typography>
            </Box>
            <Radio
              checked={false}
              size="small"
              sx={{ color: '#b0b0b0', p: 0, '&.Mui-checked': { color: environment.mainColor } }}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 1.5, px: 2.5 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          fullWidth
          sx={{
            borderColor: '#e0e0e0',
            color: '#374151',
            textTransform: 'none',
            borderRadius: '999px',
            py: 1.5,
            fontWeight: 600,
            fontFamily: 'Jost',
            fontSize: '15px',
            '&:hover': {
              backgroundColor: '#f7f7f7',
              borderColor: '#d1d5db',
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </>
  );

  // ── Schedule step: date + time slot picker ────────────────────────────────
  const renderScheduleStep = () => (
    <>
      <DialogTitle
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px 24px 16px 24px',
          background: `linear-gradient(135deg, ${environment.mainColor}15, ${environment.mainColor}08)`,
          borderBottom: `2px solid ${environment.mainColor}20`,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            backgroundColor: environment.mainColor,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            boxShadow: `0px 8px 24px ${environment.mainColor}40`,
          }}
        >
          <IoTime size={28} style={{ color: 'white' }} />
        </Box>
        <Typography
          component="h2"
          variant="h5"
          style={{
            fontWeight: 700,
            color: '#1a1a1a',
            fontSize: '24px',
            fontFamily: 'Jost, sans-serif',
            marginBottom: 8,
            letterSpacing: '-0.5px',
          }}
        >
          {t('confirmPage.Schedulerforlater')}
        </Typography>
        <Typography
          variant="body2"
          style={{
            color: '#666666',
            fontSize: '14px',
            fontFamily: 'Jost, sans-serif',
            maxWidth: 280,
            lineHeight: 1.5,
          }}
        >
          {isNotAvailableOnline
            ? 'This location is not currently available for online ordering. Please schedule your order for a future time.'
            : isStoreOpen
            ? `Choose your preferred date and time for ${
                orderType === 'pickup' ? 'pickup' : 'delivery'
              }.`
            : `This location is currently closed. Please choose your preferred date and time for ${
                orderType === 'pickup' ? 'pickup' : 'delivery'
              }.`}
        </Typography>
      </DialogTitle>

      <DialogContent
        style={{
          padding: '24px 32px 32px 32px',
          backgroundColor: '#fafafa',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            minHeight: '320px',
          }}
        >
          {/* Date selector */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%',
              alignItems: 'stretch',
              marginRight: !smDown ? '24px' : '0px',
              marginBottom: smDown ? '24px' : '0px',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography
              variant="h6"
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#333333',
                fontFamily: 'Jost, sans-serif',
                textAlign: smDown ? 'center' : 'left',
              }}
            >
              Select Date
            </Typography>
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              gap={1}
            >
              {!hasCatering && (
                <Button
                  disableRipple
                  style={{
                    width: '100%',
                    height: '64px',
                    backgroundColor:
                      sendTime === today ? environment.mainColor : 'transparent',
                    color: sendTime === today ? 'white' : '#666666',
                    border:
                      sendTime === today ? 'none' : '2px solid #e0e0e0',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '14px',
                    fontFamily: 'Jost, sans-serif',
                    textTransform: 'none',
                    boxShadow:
                      sendTime === today
                        ? `0px 4px 12px ${environment.mainColor}40`
                        : 'none',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                  variant="contained"
                  sx={{
                    '&:hover':
                      sendTime !== today
                        ? {
                            backgroundColor: '#f5f5f5 !important',
                            transform: 'translateY(-1px)',
                            borderColor: environment.mainColor,
                          }
                        : {},
                  }}
                  onClick={() => {
                    setSendTime(today);
                    setSelectedTime({});
                    setRange('');
                    setDateOfOrder(null);
                  }}
                >
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>
                    {t('confirmPage.today')}
                  </span>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      opacity: sendTime === today ? 0.9 : 0.7,
                    }}
                  >
                    {todayLabel}
                  </span>
                </Button>
              )}
              <Button
                disableRipple
                style={{
                  width: '100%',
                  height: '64px',
                  backgroundColor:
                    sendTime === tomorrow
                      ? environment.mainColor
                      : 'transparent',
                  color: sendTime === tomorrow ? 'white' : '#666666',
                  border:
                    sendTime === tomorrow ? 'none' : '2px solid #e0e0e0',
                  borderRadius: '12px',
                  fontWeight: 600,
                  fontSize: '14px',
                  fontFamily: 'Jost, sans-serif',
                  textTransform: 'none',
                  boxShadow:
                    sendTime === tomorrow
                      ? `0px 4px 12px ${environment.mainColor}40`
                      : 'none',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
                variant="contained"
                sx={{
                  '&:hover':
                    sendTime !== tomorrow
                      ? {
                          backgroundColor: '#f5f5f5 !important',
                          transform: 'translateY(-1px)',
                          borderColor: environment.mainColor,
                        }
                      : {},
                }}
                onClick={() => {
                  setSendTime(tomorrow);
                  setSelectedTime({});
                  setRange('');
                  setDateOfOrder(null);
                }}
              >
                <span style={{ fontSize: '14px', fontWeight: 600 }}>
                  {t('confirmPage.tomorrow')}
                </span>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    opacity: sendTime === tomorrow ? 0.9 : 0.7,
                  }}
                >
                  {tomorrowLabel}
                </span>
              </Button>
            </Stack>
          </div>

          {/* Time slots */}
          <div
            style={{
              flex: 1,
              width: '100%',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography
              variant="h6"
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#333333',
                marginBottom: '16px',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Available Time Slots
            </Typography>
            {hasItemAvailabilityConstraints && (
              <Typography
                variant="body2"
                sx={{
                  color: '#6B7280',
                  fontSize: '12px',
                  maxWidth: '100%',
                  marginBottom: '8px',
                  fontFamily: 'Jost, sans-serif',
                }}
              >
                {availabilityCategoryNames.length
                  ? `Some items in your cart from ${availabilityCategoryNames.join(
                      ', '
                    )} are only available during specific time windows. Time slots outside those windows have been hidden.`
                  : 'Some items in your cart are only available during specific time windows. Time slots outside those windows have been hidden.'}
              </Typography>
            )}
            {slots && slots[sendTime]?.length ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '8px',
                  maxHeight: '240px',
                  overflowY: 'auto',
                  padding: '4px',
                }}
              >
                {slots[sendTime].map((item, index) => {
                  const isSelected = range === index && sendTime === prev;

                  const formatSingleTime = (time) => {
                    if (!time || typeof time !== 'string') return time;
                    const t = time.trim();
                    if (/AM|PM/i.test(t)) {
                      const m = t.match(
                        /^(\d{1,2}):(\d{1,2})(?::\d{1,2})?\s*(AM|PM)$/i
                      );
                      if (m)
                        return `${parseInt(m[1], 10)}:${String(
                          parseInt(m[2], 10) || 0
                        ).padStart(2, '0')} ${m[3].toUpperCase()}`;
                    }
                    const parts = t.split(':');
                    let hour = parseInt(parts[0], 10);
                    const minStr = (parts[1] || '00').replace(/\s*[AP]M$/i, '');
                    const minute = String(parseInt(minStr, 10) || 0).padStart(
                      2,
                      '0'
                    );
                    if (isNaN(hour)) return t;
                    const period = hour >= 12 ? 'PM' : 'AM';
                    hour = hour % 12 || 12;
                    return `${hour}:${minute} ${period}`;
                  };
                  const formatTimeWithAMPM = (timeStr) => {
                    if (!timeStr) return timeStr;
                    if (timeStr.includes(' - ')) {
                      const [start, end] = timeStr.split(' - ');
                      return `${formatSingleTime(
                        start.trim()
                      )} - ${formatSingleTime(end.trim())}`;
                    }
                    return formatSingleTime(timeStr);
                  };

                  const displayTime = formatTimeWithAMPM(item);

                  return (
                    <Button
                      key={index + 'ty'}
                      disableRipple
                      style={{
                        height: '44px',
                        backgroundColor: isSelected
                          ? environment.mainColor
                          : 'transparent',
                        color: isSelected ? 'white' : '#333333',
                        border: isSelected ? 'none' : '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 500,
                        fontFamily: 'Jost, sans-serif',
                        textTransform: 'none',
                        boxShadow: isSelected
                          ? `0px 4px 12px ${environment.mainColor}40`
                          : 'none',
                        transition: 'all 0.2s ease',
                      }}
                      variant="contained"
                      sx={{
                        '&:hover': !isSelected
                          ? {
                              backgroundColor: '#f5f5f5 !important',
                              borderColor: `${environment.mainColor} !important`,
                              transform: 'translateY(-1px)',
                            }
                          : {},
                      }}
                      onClick={() => onTimeSelected(item, index)}
                    >
                      {displayTime}
                    </Button>
                  );
                })}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '32px 16px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <IoTime size={20} style={{ color: '#999999' }} />
                </div>
                <Typography
                  style={{
                    fontSize: '14px',
                    color: '#666666',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  {hasItemAvailabilityConstraints ||
                  lastAvailabilityCategoryNamesRef.current?.length > 0
                    ? (() => {
                        const names =
                          availabilityCategoryNames.length > 0
                            ? availabilityCategoryNames
                            : lastAvailabilityCategoryNamesRef.current || [];
                        return names.length
                          ? `Items from ${names.join(
                              ', '
                            )} are not available at any time on this day at this branch. Please choose another day or branch, or remove these items from your cart.`
                          : 'Some items in your cart are not available at any time on this day at this branch. Please choose another day or branch, or remove these items from your cart.';
                      })()
                    : sendTime === 'today'
                    ? t('confirmPage.nohourstoday')
                    : t('confirmPage.OffThisDay')}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', mb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={() => setStep('initial')}
          sx={{
            borderColor: '#d1d5db',
            color: '#374151',
            textTransform: 'none',
            borderRadius: '999px',
            px: 3,
            py: 1.5,
            fontWeight: 600,
            fontFamily: 'Jost',
            '&:hover': {
              backgroundColor: '#f7f7f7',
              borderColor: '#d1d5db',
            },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleScheduleConfirm}
          disabled={!dateOfOrder}
          sx={{
            backgroundColor: environment.mainColor,
            color: 'white',
            textTransform: 'none',
            borderRadius: '999px',
            px: 4,
            py: 1.5,
            fontWeight: 600,
            fontFamily: 'Jost',
            '&:hover': {
              backgroundColor: environment.mainColor,
              opacity: 0.9,
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#666',
            },
          }}
        >
          Confirm Time
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      fullWidth={smDown}
      maxWidth="sm"
      PaperProps={{
        style: {
          borderRadius: '24px',
          boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.15)',
          border: 'none',
          overflow: 'hidden',
          background: 'white',
          minHeight: step === 'initial' ? '360px' : '500px',
        },
      }}
    >
      {/* Close button */}
      <div
        style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          zIndex: 1000,
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onClick={handleClose}
        >
          <CancelRoundedIcon style={{ cursor: 'pointer', color: '#666666', fontSize: '20px' }} />
        </div>
      </div>

      {step === 'initial' ? renderInitialStep() : renderScheduleStep()}
    </Dialog>
  );
};

export default SchedulingDialog;
